/**
 * AES-256-GCM authenticated encryption using @noble/ciphers.
 *
 * The output format is: ciphertext || tag (standard convention for noble/ciphers gcm).
 * The 16-byte GCM authentication tag is appended to the ciphertext bytes.
 *
 * This implementation is byte-identical to WebCrypto AES-GCM, so artifacts
 * sealed by the pipeline (Node) can be opened in the viewer (browser).
 */

import { gcm } from '@noble/ciphers/aes.js';

/** IV must be exactly 12 bytes for AES-GCM. */
const IV_LENGTH = 12;

/** AES-256-GCM key must be exactly 32 bytes. */
const KEY_LENGTH = 32;

/**
 * Seals plaintext under the given key and IV.
 * Returns ciphertext with the 16-byte GCM tag appended.
 */
export function seal(
  plaintext: Uint8Array,
  key: Uint8Array,
  iv: Uint8Array,
): Uint8Array {
  if (key.length !== KEY_LENGTH) {
    throw new Error(
      `seal(): key must be ${KEY_LENGTH} bytes, got ${key.length}`,
    );
  }
  if (iv.length !== IV_LENGTH) {
    throw new Error(
      `seal(): IV must be ${IV_LENGTH} bytes, got ${iv.length}`,
    );
  }

  const aes = gcm(key, iv);
  return aes.encrypt(plaintext);
}

/**
 * Opens a sealed ciphertext (ciphertext || tag) using the given key and IV.
 * Throws if authentication fails (tampered ciphertext, wrong key, wrong IV).
 */
export function open(
  ciphertext: Uint8Array,
  key: Uint8Array,
  iv: Uint8Array,
): Uint8Array {
  if (key.length !== KEY_LENGTH) {
    throw new Error(
      `open(): key must be ${KEY_LENGTH} bytes, got ${key.length}`,
    );
  }
  if (iv.length !== IV_LENGTH) {
    throw new Error(
      `open(): IV must be ${IV_LENGTH} bytes, got ${iv.length}`,
    );
  }

  try {
    const aes = gcm(key, iv);
    return aes.decrypt(ciphertext);
  } catch (err) {
    throw new Error(
      'AES-256-GCM authentication failed: the ciphertext has been tampered ' +
        'with, or the key / IV is incorrect.',
      { cause: err },
    );
  }
}
